import { Key, useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Accordion, AccordionSummary, AccordionDetails, Typography, Button, useMediaQuery } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import useBackendApi from '../hooks/useBackendApi';
import { HeaderOptions, RequestOptions } from '../types/httpRequests';
import GradientCircularProgressContainer from '../components/common/GradientCircularProgressContainer';
import HeaderBanner from '../components/common/HeaderBanner';
import ContactForm from '../components/common/ContactForm';
import useSnackBarAlertMessages from '../hooks/useSnackBarAlertMessages';
import CustomTitleWithBlueBackground from '../components/common/CustomTitleWithBlueBackground';
import { colors } from '../styles/fontStyles';
import { UNAUTHENTICATED } from '../constants/user';
import { useThemeContext } from '../contexts/ThemeContext';
import type { TurnstileInstance } from '@marsidev/react-turnstile';

const backendURL = process.env.REACT_APP_BACKEND_URL;
const shopifyAccessToken = process.env.REACT_APP_SHOPIFY_STOREFRONT_API_TOKEN;

type Article = {
  title: string;
  body: string;
};

type ArticleData = {
  [category: string]: Article[];
};

type FormDataAttributes = {
  subject: string;
  message: string;
  email: string;
  name: string;
};

const SupportContent = () => {
  const { isLoading, apiFetchRequest } = useBackendApi();
  const [articleData, setArticleData] = useState<ArticleData>({});
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | false>(false);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const turnstileRef = useRef<TurnstileInstance>(null);
  const { userType } = useThemeContext();

  const isUserGuest = userType === UNAUTHENTICATED;

  const [formData, setFormData] = useState<FormDataAttributes>({
    subject: '',
    message: '',
    name: '',
    email: '',
  } as FormDataAttributes);

  const navigate = useNavigate();

  const { addAlertMessages, renderSnackBarAlertComponent } = useSnackBarAlertMessages();

  let headers: HeaderOptions = {
    'x-shop-key': shopifyAccessToken,
  };

  const { theme } = useThemeContext();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm')); // Mobile query

  useEffect(() => {
    const apiRequestOptions: RequestOptions = {
      endpoint: `${backendURL}/support/articles`,
      headers: headers,
    };

    const fetchData = async () => {
      try {
        const data = await apiFetchRequest(apiRequestOptions);
        if (data.status !== 200) {
          return navigate('/404.html');
        }

        setArticleData(data.body.articles || {});

        // Set the first category as the default selected category
        const firstCategory = Object.keys(data.body.articles)[0];
        setSelectedCategory(firstCategory || null);
      } catch (err) {
        console.error('Error fetching support articles', err);
      }
    };

    fetchData();
  }, []);

  const handleFormSubmit = async () => {
    const { subject, message, email, name } = formData;
    let additionalData = {};
    if (isUserGuest) additionalData = { name, email };

    if (!subject || !message || (isUserGuest && (!name || !email))) {
      addAlertMessages({
        message: 'Please enter all fields.',
        severityType: 'error',
      });
      return;
    }

    if (!turnstileToken) {
      addAlertMessages({
        message: 'Please complete the security check.',
        severityType: 'error',
      });
      return;
    }

    let reqData = {
      subject,
      message,
      ...additionalData,
      turnstileToken,
    };

    const apiRequestOptions: RequestOptions = {
      endpoint: `${backendURL}/support/ticket`,
      method: 'POST',
      body: reqData,
      headers: headers,
    };

    try {
      const response = await apiFetchRequest(apiRequestOptions);
      if (response.status === 200) {
        addAlertMessages({
          message: 'Submitted your data',
          severityType: 'success',
        });
        // Reset form fields after successful submission
        setFormData({
          subject: '',
          message: '',
          name: '',
          email: '',
        });
        setTurnstileToken(null);
        turnstileRef.current?.reset();
      } else {
        addAlertMessages({
          message: 'Failed to submit data',
          severityType: 'error',
        });
      }
    } catch (err) {
      console.error('Error submitting the contact form', err);
      addAlertMessages({
        message: 'Failed to submit data',
        severityType: 'error',
      });
    }
  };

  if (isLoading) {
    return <GradientCircularProgressContainer />;
  }

  const handleCategoryClick = (category: string) => {
    setSelectedCategory(category);
  };

  const handleAccordionChange = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded ? panel : false);
  };

  const renderArticleBodyContent = (article: Article) => {
    const decoratedArticleBody = article.body
      .replace(/<a/g, `<a style="color: ${theme.support.articleDetails.body.links}"`) // Change links color
      .replace(/#000000/g, `${theme.support.articleDetails.body.text}`) // Replace black color with white color
      .replace(/<p>/g, `<p style='margin: 0'>`); // Replace styles on paragraph

    return (
      <Typography
        sx={{
          color: theme.support.articleDetails.body.text,
          fontSize: '12px',
          fontStyle: 'normal',
          fontWeight: 400,
          lineHeight: '16px',
        }}
        dangerouslySetInnerHTML={{
          __html: decoratedArticleBody,
        }}
      />
    );
  };

  const renderArticleDetails = (article: { title: any; body: any }, index: Key) => {
    return (
      <Accordion
        key={index}
        expanded={expanded === `panel-${index}`}
        onChange={handleAccordionChange(`panel-${index}`)}
        sx={{
          display: 'flex',
          padding: '16px',
          flexDirection: 'column',
          alignItems: 'flex-start',
          gap: 'var(--Spacing-1, 4px)',
          alignSelf: 'stretch',
          borderRadius: '16px',
          background: theme.support.articleDetails.background,
          marginBottom: '16px',
          '&:last-child': {
            marginBottom: '0', // Remove margin for the last accordion
          },
          '&::before': {
            display: 'none', // Removes the default line separator
          },
          '&.Mui-expanded': {
            background: theme.support.articleDetails.backgroundExpanded,
            border: theme.support.articleDetails.border,
          },
          '&.MuiAccordion-root': {
            boxShadow: 'none',
          },
          '&.MuiAccordion-root:first-of-type, &.MuiAccordion-root:last-of-type': {
            borderRadius: '16px',
          },
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon sx={{ color: colors.primaryBlue }} />}
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            alignSelf: 'stretch',
            height: '48px',
            '&.Mui-expanded .MuiAccordionSummary-content': {
              margin: 0,
            },
            '&.Mui-expanded': {
              minHeight: '48px',
            },
          }}
        >
          <Typography
            sx={{
              color: expanded === `panel-${index}` ? theme.support.articleDetails.title.expandedColor : theme.support.articleDetails.title.notExpandedColor,
              fontSize: '16px',
              fontStyle: 'normal',
              fontWeight: 500,
              lineHeight: '24px',
              letterSpacing: '0.15px',
            }}
          >
            {article.title}
          </Typography>
        </AccordionSummary>
        <AccordionDetails
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            alignSelf: 'stretch',
            paddingTop: '8px',
          }}
        >
          {renderArticleBodyContent(article)}
        </AccordionDetails>
      </Accordion>
    );
  };

  const renderArticles = (category: string) => {
    if (!articleData[category]) {
      return <Typography>No articles available for this category.</Typography>;
    }

    return articleData[category].map((article, index) => renderArticleDetails(article, index));
  };

  const SupportArticles = () => {
    const categories = Object.keys(articleData);

    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          width: isMobile ? '100%' : '60%',
          alignItems: 'flex-start',
          gap: '16px',
          marginTop: isMobile ? '4px' : '',
        }}
      >
        <CustomTitleWithBlueBackground text="Frequently Asked Questions" />
        <Typography
          variant="body1"
          gutterBottom
          sx={{
            gap: '8px',
            color: theme.support.description.color,
            fontSize: '14px',
            fontStyle: 'normal',
            fontWeight: '400',
            lineHeight: '20px',
            letterSpacing: '0.25px',
          }}
        >
          These are the most commonly asked questions about Relentless Health
        </Typography>
        <Box
          sx={{
            display: 'flex',
            gap: '10px',
            flexWrap: isMobile ? 'nowrap' : 'wrap',
            overflowX: isMobile ? 'auto' : 'unset',
            width: '100%',
          }}
        >
          {categories.map((category, index) => (
            <Button
              key={index}
              onClick={() => handleCategoryClick(category)}
              sx={{
                display: 'flex',
                minWidth: '107px',
                height: '32px',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                borderRadius: '100px',
                background: selectedCategory === category ? theme.support.categoryButton.buttonSelected.backgroundColor : theme.support.categoryButton.buttonNotSelected.backgroundColor,
                color: selectedCategory === category ? theme.support.categoryButton.buttonSelected.color : theme.support.categoryButton.buttonNotSelected.color,
                textAlign: 'center',
                fontSize: '12px',
                fontStyle: 'normal',
                fontWeight: selectedCategory === category ? 600 : 500,
                lineHeight: '16px',
                letterSpacing: '0.5px',
                border: selectedCategory === category ? 'none' : `1px solid ${theme.support.categoryButton.buttonNotSelected.borderColor}`,
                cursor: 'pointer',
                transition: 'background 0.3s, color 0.3s',
                '&:hover': {
                  background: selectedCategory === category ? theme.support.categoryButton.buttonSelectedHover.backgroundColor : theme.support.categoryButton.buttonNotSelectedHover.backgroundColor,
                  color: selectedCategory === category ? theme.support.categoryButton.buttonSelectedHover.color : theme.support.categoryButton.buttonNotSelectedHover.color,
                },
                textTransform: 'none',
              }}
            >
              {category}
            </Button>
          ))}
        </Box>
        <Box sx={{ mt: 2 }}>{selectedCategory && <Box role="tabpanel">{renderArticles(selectedCategory)}</Box>}</Box>
      </Box>
    );
  };

  return (
    <Box
      style={{
        backgroundColor: theme.support.backgroundColor,
        padding: '40px',
        borderRadius: '16px',
      }}
      >

      <HeaderBanner
        imageSrc={'/images/support.png'}
        title="Support"
        subtitle="How can we help you?"
        description="Take a look to our FAQs or contact our support team."
        linkText={undefined}
        linkUri={undefined}
      />

      <Box
        sx={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          gap: '32px',
          alignItems: isMobile ? 'center' : 'flex-start',
        }}
      >
        <SupportArticles />

        <Box
          sx={{
            width: isMobile ? '100%' : '463px',
          }}
        >
          <ContactForm
            imageSrc={'/images/support-contact.png'}
            formData={formData}
            setFormData={setFormData}
            onSubmit={handleFormSubmit}
            showNameEmail={isUserGuest}
            onTurnstileSuccess={(token: string) => setTurnstileToken(token)}
            turnstileRef={turnstileRef}
          />
        </Box>
      </Box>

      {renderSnackBarAlertComponent()}
    </Box>
  );
};

export default SupportContent;
